/**
 * ContextScenario component manages the workspace, scenario, and origin selection
 * with Apollo GraphQL integration and route-based state management.
 *
 * Key features:
 * - Three-level context hierarchy (workspace → scenario → origin)
 * - Route-synchronized state management
 * - Calendar range caching per scenario
 * - Role-based navigation handling
 * - Mouseflow tracking integration
 * - Modal-based context switching
 */

import React, { useState, useEffect, useContext, FC, useMemo } from "react";
import cx from "classnames";
import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { withRouter, RouteComponentProps, useLocation } from "react-router-dom";
import { Pill, SkeletonBase, Text, Tooltip } from "@foris/avocado-suite";
import { Icon } from "@foris/avocado-icons";
import { Module, Origin, Scenario, Workspace } from "@models/ISchema";
import { getCacheRangesByScenario } from "@config/CalendarRanges/calendarRanges";
import { ContextEDH } from "@context/ContextEDH";
import contextualizadorBase from "./graphql/contextualizadorBase.query.graphql";
import useContextUrl from "@common/hooks/useContextUrl";
import ContextModal from "@common/layout/components/context-modal";
import css from "./contextScenario.module.scss";
import {
  setContextualizadorData,
  setIsLoadingContextualizador,
} from "@context/useContextualizadorStore";
import useBookingAccess from "@common/hooks/useBookingAccess";
import TourTooltip from "@common/components/tour-tooltip";
import { Step } from "react-joyride";
import {
  setIsOnboardingOpen,
  shouldShowHotspot,
  useOnboardingStore,
} from "@context/useOnboardingStore";

/**
 * Props interface for ContextScenario component
 * @extends RouteComponentProps for router integration
 * @property {any} history - Router history for navigation
 * @property {function} onSetCalendarRanges - Optional callback for calendar range updates
 */
interface ContextScenarioProps extends RouteComponentProps<any> {
  history: any;
  onSetCalendarRanges?: any;
  onContextModalOpen?: (isOpen: boolean) => void;
}

type currentOrigin = {
  label: string;
  value: string;
  self: Origin;
};

const voidSelect = {
  options: [{ label: "", value: "", self: null }],
  value: {
    value: "",
    label: "",
    self: null,
  },
};

const ContextScenario: FC<ContextScenarioProps> = props => {
  const client = useApolloClient();
  const location = useLocation();
  const isInBooking = location?.pathname.includes("booking");
  const { isAdminBooking } = useBookingAccess();
  const { state } = useContext(ContextEDH);
  const { workspace, origin, scenario } = useContextUrl();
  const [changeCx, setChangeCx] = useState(
    (origin === "null" || !origin) && !scenario && !isInBooking,
  );
  const [workspaces, setWorkspaces] = useState(null);
  const [scenarios, setScenarios] = useState(null);
  const [origins, setOrigins] = useState(null);
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [currentScenario, setCurrentScenario] = useState(null);
  const [currentOrigin, setCurrentOrigin] = useState<currentOrigin | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const onboardingsSeen = useOnboardingStore(state => state.onboardingsSeen);

  const { t } = useTranslation();

  const showHotspot = useMemo(() => {
    return shouldShowHotspot("header-context") && !changeCx;
  }, [onboardingsSeen, changeCx]);

  const canAccessEDH = useMemo(
    () =>
      state?.base?.base?.modules?.some(
        (module: Module) => module?.accesible && module?.code === "scheduler_editor",
      ),
    [state?.base?.base?.modules],
  );

  /**
   * Return select options
   * @param data ''
   * @param findValue ''
   */
  /**
   * Transforms raw context data into select-compatible options and finds current value
   * @param {Array<any>} data - Raw context data array
   * @param {number} findValue - Current value to find in options
   * @returns {Object} Object containing options array and selected value
   */
  const getContextValues = (data: Array<any>, findValue: number): any => {
    const options = data.map((value: any) => {
      return { label: value.name, value: value.id, self: value };
    });
    const value = options.find((item: any) => parseInt(item.value) === findValue);

    return {
      options,
      value,
    };
  };

  /**
   * Change states
   * @param moduleConfig ''
   */
  /**
   * Handles context changes and updates related state
   * - Updates calendar ranges based on selected scenario
   * - Updates current context state
   * - Handles navigation based on user permissions
   * - Tracks context changes in Mouseflow
   *
   * @param {Object} newContext - New context values to save
   */
  const handleSaveContext = async (newContext: any, hasSameContext: boolean) => {
    try {
      !hasSameContext && setIsLoading(true);
      const { history } = props;
      const context = newContext.context;
      const { scenario, workspace, origin } = context;
      // set ranges calendar by scenario
      const newRanges = await getCacheRangesByScenario(scenario.value, client);
      if (newRanges) {
        localStorage.setItem("range-start-time", newRanges.start);
        localStorage.setItem("range-end-time", newRanges.end);
      }
      // Set current context
      setCurrentWorkspace(workspace);
      setCurrentScenario(scenario);
      setCurrentOrigin(origin ?? null);
      // Redirect
      const edhContext = `${workspace?.value}/${scenario?.value}${
        origin?.value ? `/${origin?.value}` : ""
      }`;

      const abilities = state?.base?.base?.user?.abilities;
      const canAccessGroupManager = abilities?.can_access_group_manager;
      const isGroupManager = location?.pathname.includes("groups-manager");
      const shouldRedirectHome =
        !canAccessGroupManager ||
        !origin?.value ||
        !canAccessEDH ||
        (canAccessGroupManager && !isGroupManager);

      if (shouldRedirectHome) {
        history.replace(`/home/${edhContext}`);
      } else {
        !hasSameContext && window.dispatchEvent(new CustomEvent("reset-groups-manager"));
        !hasSameContext && history.replace(`/editor/groups-manager/${edhContext}`);
      }

      window.dispatchEvent(
        new CustomEvent("update-data-base", {
          detail: {
            userID: state?.base?.base?.user?.id,
            scenarioId: scenario?.value,
          },
        }),
      );
      !hasSameContext && setIsLoading(false);
    } catch (error) {
      console.error(error);
      !hasSameContext && setIsLoading(false);
    }
  };

  const handleSetCurrentContext = (
    workspaceList: Workspace[] = [],
    scenarioList: Scenario[] = [],
    originList: Origin[] = [],
  ) => {
    const workspaces = getContextValues(workspaceList, parseInt(workspace));
    const scenarios = getContextValues(scenarioList, parseInt(scenario));
    const optionOrigins: currentOrigin[] = originList.map((value: any) => {
      return { label: `${value.id} - ${value.label}`, value: value.id, self: value };
    });

    const origins = origin
      ? {
          options: optionOrigins,
          value:
            optionOrigins.find((item: any) => parseInt(item.value) === parseInt(origin)) || null,
        }
      : voidSelect;

    setWorkspaces(workspaces);
    setScenarios(scenarios);
    setOrigins(origins);
    setCurrentWorkspace(workspaces.value);
    setCurrentScenario(scenarios.value);
    setCurrentOrigin(origins.value);

    const originIsVisible = !!origins?.value?.self
      ? "attributes" in origins?.value?.self
        ? origins?.value?.self?.attributes?.isVisible
        : origins?.value?.self?.isVisible
      : false;

    if (!!origins?.value?.self && !originIsVisible) {
      props?.history?.replace(`/home/${currentWorkspace?.value}/${currentScenario?.value}`);
    }
  };

  /**
   * first query context
   */
  /**
   * Fetches initial context data using GraphQL
   * - Retrieves workspaces, scenarios, and origins
   * - Filters origins by ASSIGNATION type and visibility
   * - Sets up initial context state
   * - Handles data transformation for select components
   */
  const getQueryContext = async () => {
    try {
      setIsLoading(true);
      setIsLoadingContextualizador(true);

      const variablesQuery = {
        query: contextualizadorBase,
        variables: {
          scenarioFilter: {
            workspaceId: parseInt(workspace),
          },
          scenarioId: scenario ? parseInt(scenario) : undefined,
          originFilter: {
            type: "ASSIGNATION",
            isVisible: true,
          },
        },
      };
      const { data, loading } = await client.query(variablesQuery);

      if (data && !loading) {
        handleSetCurrentContext(data.base.workspaces, data.base.scenarios, data.data.origins);
      }

      setContextualizadorData({
        workspaces: data?.base?.workspaces ?? [],
        scenarios: data?.base?.scenarios ?? [],
        origins: data?.data?.origins ?? [],
      });
      setIsLoadingContextualizador(false);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.log(error);
    }
  };

  useEffect(() => {
    getQueryContext();
  }, [workspace, scenario, origin]);

  useEffect(() => {
    props?.onContextModalOpen?.(changeCx);
  }, [changeCx]);

  const isWarning =
    ((canAccessEDH && !!currentOrigin?.self && !currentOrigin?.self?.isProductive) ||
      (!canAccessEDH &&
        !!currentScenario?.self &&
        currentScenario?.self?.status?.value !== "PRODUCTIVE")) &&
    !isLoading;
  const hasIconPill =
    ((canAccessEDH && !!currentOrigin?.self) || (!canAccessEDH && !!currentScenario?.self)) &&
    !isLoading;

  if (isInBooking && !isAdminBooking) {
    // If in booking context and no origin is set, do not render the component
    return null;
  }

  const steps: Step[] = [
    {
      content: t("onboarding.header-context.description"),
      target: ".contextTour",
      locale: {
        last: t("onboarding.actions.done"),
      },
      title: t("onboarding.header-context.title"),
      disableBeacon: false,
      placement: "bottom",
      placementBeacon: "left-end",
    },
  ];

  return (
    <>
      {!isLoading && showHotspot && (
        <TourTooltip
          // tooltipClassName="sideMenuTour"
          beaconClassName={css.contextTourBeacon}
          onClose={() => {
            setIsOnboardingOpen(false, "header-context");
          }}
          onToolipOpen={() => {
            setIsOnboardingOpen(true, "header-context");
          }}
          startTour={showHotspot}
          steps={steps}
          spotlightPadding={4}
        />
      )}

      <Tooltip
        className={css.contextTooltip}
        visible={isLoading ? false : undefined}
        label={
          <div className={css.contextTooltip_content}>
            {canAccessEDH && (
              <>
                {!!currentOrigin?.value && (
                  <Text>
                    {t("context-modal.origin")}: {currentOrigin?.label ?? "-"}
                  </Text>
                )}
                <Text>
                  {t("context-modal.workspace")}: {currentWorkspace?.label ?? "-"}
                </Text>
                <Text>
                  {t("context-modal.scenario")}: {currentScenario?.label ?? "-"}
                </Text>
              </>
            )}

            {!canAccessEDH && (
              <>
                <Text>
                  <strong>{t("context-modal.workspace")}:</strong> {currentWorkspace?.label ?? "-"}
                </Text>
                <Text>
                  <strong>{t("context-modal.details.scenario-id-short")}:</strong>{" "}
                  {currentScenario?.value ?? "-"}
                </Text>
              </>
            )}

            {hasIconPill && (
              <Pill
                className={css.contentPill}
                color="neutral-00"
                icon={isWarning ? "alert-triangle" : "star"}
                iconFilled
              >
                {isWarning
                  ? t("context-modal.details.non-productive")
                  : t("context-modal.details.productive")}
              </Pill>
            )}
          </div>
        }
        placement="bottomRight"
      >
        <div
          className={cx(
            "contextTour",
            css.contextScenario,
            changeCx && css.contextScenario__active,
            isWarning && css.contextScenario__warning,
            hasIconPill && css.contextScenario__hasPill,
          )}
          onClick={(): void => setChangeCx(true)}
        >
          <div className={css.contextScenario_label}>
            <Text className={css.labelType} type="xs" weight="medium">
              {canAccessEDH && !!currentOrigin?.self
                ? t("context-modal.origin")
                : t("context-modal.scenario")}
              :
            </Text>
            <Text className={css.labelText} type="sm">
              {isLoading ? (
                <SkeletonBase height="14px" width="96px" />
              ) : (
                <>
                  {canAccessEDH && !!currentOrigin?.self
                    ? (currentOrigin?.label ?? "")
                    : (currentScenario?.label ?? "")}
                </>
              )}
            </Text>
          </div>

          {hasIconPill && (
            <Pill className={css.contextScenario_pill} color={isWarning ? "warning-10" : undefined}>
              <Icon
                name={isWarning ? "alert-triangle" : "star"}
                size="sm"
                color={isWarning ? "warning-50" : "neutral-70"}
                filled
              />
            </Pill>
          )}
        </div>
      </Tooltip>

      {workspaces && scenarios && (
        <ContextModal
          open={changeCx}
          workspace={workspaces}
          scenario={scenarios}
          origin={origins}
          canAccessEDH={canAccessEDH}
          onSave={(newContext: any, hasSameContext: boolean) =>
            handleSaveContext(newContext, hasSameContext)
          }
          onClose={(): void => setChangeCx(false)}
        />
      )}
    </>
  );
};

export default withRouter(ContextScenario);
